import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Home,
  Building2,
  Tractor,
  Warehouse,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Phone as PhoneIcon,
  MapPin,
  Loader2,
  Zap,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { usePageCode } from "../../config/PageContext";
import { trackFormStart, trackGenerateLead, trackEvent } from "../../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HOUSE_TYPES = [
  { value: "wolnostojacy", label: "Dom wolnostojący", desc: "Niezależny budynek jednorodzinny", icon: Home },
  { value: "blizniak", label: "Bliźniak", desc: "Dom dwurodzinny / połówka", icon: Building2 },
  { value: "szeregowiec", label: "Szeregowiec", desc: "Zabudowa szeregowa", icon: Warehouse },
  { value: "gospodarstwo", label: "Gospodarstwo rolne", desc: "Dom z budynkami gospodarczymi", icon: Tractor },
];

const BILL_RANGES = [
  { value: "below_300", label: "Poniżej 300 zł", desc: "Małe zużycie energii" },
  { value: "300_500", label: "300 – 500 zł", desc: "Typowe dla pary / małej rodziny" },
  { value: "500_800", label: "500 – 800 zł", desc: "Standardowe dla rodziny 4-osobowej" },
  { value: "above_800", label: "Powyżej 800 zł", desc: "Duże zużycie / pompa ciepła" },
];

const STEPS = ["Typ domu", "Rachunki", "Kod pocztowy", "Telefon"];

const initialData = {
  house_type: "",
  monthly_bill: "",
  postal_code: "",
  phone: "",
};

export default function QuizDialog({ open, onOpenChange }) {
  const pageCode = usePageCode();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset on close
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(0);
        setData(initialData);
        setErrors({});
        setSubmitted(false);
        setSubmitting(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Scroll dialog body to top whenever step changes (mobile UX)
  useEffect(() => {
    if (!open) return;
    const el = document.querySelector('[data-testid="quiz-scroll-area"]');
    if (el) el.scrollTop = 0;
  }, [step, submitted, open]);

  // Fire form_start when the quiz dialog opens
  useEffect(() => {
    if (open) trackFormStart("quiz");
  }, [open]);

  const goToHeroForm = () => {
    trackEvent("quiz_abandoned_to_hero");
    onOpenChange(false);
    setTimeout(() => {
      const el = document.getElementById("lead-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      const input = document.getElementById("hero-phone");
      setTimeout(() => input?.focus(), 500);
    }, 200);
  };

  const update = (key, value) => {
    setData((d) => ({ ...d, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const formatPostal = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 5);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  };

  const validateStep = () => {
    const e = {};
    if (step === 0 && !data.house_type) e.house_type = "Wybierz typ budynku";
    if (step === 1 && !data.monthly_bill) e.monthly_bill = "Wybierz przedział";
    if (step === 2 && !/^\d{2}-\d{3}$/.test(data.postal_code))
      e.postal_code = "Wpisz kod w formacie XX-XXX";
    if (step === 3) {
      const cleaned = data.phone.replace(/[\s\-\(\)]/g, "");
      if (!/^(\+48)?\d{9}$/.test(cleaned)) e.phone = "Numer powinien mieć 9 cyfr";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep()) return;
    if (step === 0) trackEvent("quiz_step1_complete", { house_type: data.house_type });
    else if (step === 1) trackEvent("quiz_step2_complete", { bill_range: data.monthly_bill });
    else if (step === 2) trackEvent("quiz_step3_complete", { postal_code: data.postal_code });
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else submit();
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        house_type: data.house_type,
        monthly_bill: data.monthly_bill,
        postal_code: data.postal_code,
        phone: data.phone.replace(/[\s\-\(\)]/g, ""),
        page: pageCode,
        source: "Quiz",
        url: typeof window !== "undefined" ? window.location.href : null,
      });
      trackGenerateLead("quiz");
      setSubmitted(true);
      toast.success("Dziękujemy! Oddzwonimy w 5–10 minut.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : detail || "Coś poszło nie tak. Spróbuj ponownie.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const progress = submitted ? 100 : ((step + 1) / STEPS.length) * 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden bg-white border-0 sm:rounded-3xl max-h-[92vh] flex flex-col"
        data-testid="quiz-dialog"
      >
        <DialogTitle className="sr-only">Kalkulator oszczędności Heliora</DialogTitle>
        <DialogDescription className="sr-only">
          Quiz w 4 krokach, aby otrzymać bezpłatną wycenę instalacji fotowoltaicznej.
        </DialogDescription>

        {/* Header / progress */}
        <div className="px-6 pt-6 sm:px-8 sm:pt-8 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-brand-blue">
              <Zap className="h-4 w-4" />
              <span className="text-xs font-bold uppercase tracking-[0.18em]">
                {submitted ? "Gotowe" : `Krok ${step + 1} z ${STEPS.length}`}
              </span>
            </div>
            {!submitted && (
              <span className="text-xs text-brand-muted">{STEPS[step]}</span>
            )}
          </div>
          <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-blue rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
              data-testid="quiz-progress"
            />
          </div>
        </div>

        {/* Body (scrollable) */}
        <div
          className="px-6 sm:px-8 py-6 sm:py-8 flex-1 overflow-y-auto"
          data-testid="quiz-scroll-area"
        >
          {submitted ? (
            <SuccessView />
          ) : step === 0 ? (
            <StepCardChoice
              title="Jaki masz dom?"
              subtitle="Wybierz typ budynku, dla którego chcesz wycenę."
              options={HOUSE_TYPES}
              value={data.house_type}
              onChange={(v) => update("house_type", v)}
              testIdPrefix="house-type"
              error={errors.house_type}
            />
          ) : step === 1 ? (
            <StepCardChoice
              title="Ile płacisz miesięcznie za prąd?"
              subtitle="Pomoże nam to oszacować potencjalne oszczędności."
              options={BILL_RANGES}
              value={data.monthly_bill}
              onChange={(v) => update("monthly_bill", v)}
              testIdPrefix="bill"
              error={errors.monthly_bill}
            />
          ) : step === 2 ? (
            <div data-testid="step-postal">
              <h3 className="font-heading font-bold text-2xl sm:text-3xl text-brand-ink tracking-tight">
                Jaki jest Twój kod pocztowy?
              </h3>
              <p className="mt-2 text-brand-muted">
                Sprawdzimy nasłonecznienie oraz warunki dla instalacji w Twojej okolicy.
              </p>
              <div className="mt-6 max-w-sm">
                <label className="text-sm font-semibold text-brand-ink flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-brand-blue" />
                  Kod pocztowy
                </label>
                <Input
                  data-testid="postal-input"
                  inputMode="numeric"
                  placeholder="00-000"
                  value={data.postal_code}
                  maxLength={6}
                  onChange={(e) => update("postal_code", formatPostal(e.target.value))}
                  onKeyDown={(e) => e.key === "Enter" && next()}
                  className="mt-2 h-14 rounded-xl text-lg font-medium border-2 border-brand-border focus-visible:ring-2 focus-visible:ring-brand-blue/30 focus-visible:border-brand-blue"
                />
                {errors.postal_code && (
                  <p className="mt-2 text-sm text-red-600" data-testid="postal-error">
                    {errors.postal_code}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div data-testid="step-phone">
              <h3 className="font-heading font-bold text-2xl sm:text-3xl text-brand-ink tracking-tight">
                Gdzie wysłać bezpłatną wycenę?
              </h3>
              <p className="mt-2 text-brand-muted">
                Skontaktuje się z Tobą doradca i przekaże wstępną wycenę oraz dalsze kroki.
              </p>
              <div className="mt-6 max-w-sm">
                <label className="text-sm font-semibold text-brand-ink flex items-center gap-2">
                  <PhoneIcon className="h-4 w-4 text-brand-blue" />
                  Numer telefonu
                </label>
                <Input
                  data-testid="phone-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="np. 600 000 000"
                  value={data.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && next()}
                  className="mt-2 h-14 rounded-xl text-lg font-medium border-2 border-brand-border focus-visible:ring-2 focus-visible:ring-brand-blue/30 focus-visible:border-brand-blue"
                />
                {errors.phone && (
                  <p className="mt-2 text-sm text-red-600" data-testid="phone-error">
                    {errors.phone}
                  </p>
                )}
                <p className="mt-3 text-xs text-brand-muted leading-relaxed">
                  Klikając "Otrzymaj bezpłatną wycenę" wyrażasz zgodę na kontakt telefoniczny w sprawie oferty.
                  Twoje dane są bezpieczne i nie udostępniamy ich osobom trzecim.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer / nav */}
        {!submitted && (
          <div className="px-6 sm:px-8 pb-6 sm:pb-7 pt-3 border-t border-brand-border/60 bg-white flex-shrink-0">
            <div className="flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={back}
                disabled={step === 0 || submitting}
                data-testid="quiz-back-btn"
                className="h-12 rounded-full text-brand-muted hover:text-brand-ink hover:bg-slate-100 disabled:opacity-0"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Wstecz
              </Button>
              <Button
                type="button"
                onClick={next}
                disabled={submitting}
                data-testid="quiz-next-btn"
                className="group h-12 rounded-full bg-brand-yellow hover:bg-brand-yellowHover text-brand-yellowFg font-bold px-6 sm:px-7 shadow-md hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wysyłanie...
                  </>
                ) : step === STEPS.length - 1 ? (
                  <>Otrzymaj bezpłatną wycenę <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" /></>
                ) : (
                  <>Dalej <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" /></>
                )}
              </Button>
            </div>
            <button
              type="button"
              onClick={goToHeroForm}
              data-testid="quiz-prefer-callback-link"
              className="mt-3 w-full text-center text-sm text-brand-muted hover:text-brand-blue transition-colors"
            >
              Nie chcesz wypełniać quizu? <span className="underline font-semibold">Zostaw numer — oddzwonimy</span>
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StepCardChoice({ title, subtitle, options, value, onChange, testIdPrefix, error }) {
  return (
    <div data-testid={`step-${testIdPrefix}`}>
      <h3 className="font-heading font-bold text-2xl sm:text-3xl text-brand-ink tracking-tight">
        {title}
      </h3>
      <p className="mt-2 text-brand-muted">{subtitle}</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((opt) => {
          const Icon = opt.icon;
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              data-testid={`${testIdPrefix}-${opt.value}`}
              className={`text-left border-2 rounded-2xl p-4 sm:p-5 transition-all flex items-center gap-4 group ${
                active
                  ? "border-brand-blue bg-brand-surface ring-4 ring-brand-blue/10"
                  : "border-brand-border hover:border-brand-blue/50 hover:bg-brand-surface/60"
              }`}
            >
              {Icon && (
                <span
                  className={`grid place-items-center h-11 w-11 rounded-xl flex-shrink-0 transition-colors ${
                    active ? "bg-brand-blue text-white" : "bg-brand-surface text-brand-blue"
                  }`}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
              )}
              <span className="flex-1">
                <span className="block font-heading font-semibold text-brand-ink">
                  {opt.label}
                </span>
                <span className="block text-sm text-brand-muted mt-0.5">{opt.desc}</span>
              </span>
              {active && <CheckCircle2 className="h-5 w-5 text-brand-blue flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      {error && (
        <p className="mt-3 text-sm text-red-600" data-testid={`${testIdPrefix}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

function SuccessView() {
  return (
    <div className="text-center py-6" data-testid="quiz-success">
      <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-emerald-50 text-emerald-600">
        <CheckCircle2 className="h-9 w-9" strokeWidth={1.8} />
      </div>
      <h3 className="mt-5 font-heading font-bold text-2xl sm:text-3xl text-brand-ink tracking-tight">
        Dziękujemy! Twoje zgłoszenie trafiło do nas.
      </h3>
      <p className="mt-3 text-brand-muted max-w-md mx-auto">
        Doradca Heliora oddzwoni do Ciebie <strong className="text-brand-ink">w ciągu 5–10 minut</strong>{" "}
        z bezpłatną, indywidualną wyceną instalacji fotowoltaicznej.
      </p>
      <div className="mt-6 inline-flex items-center gap-2 text-sm text-brand-muted bg-brand-surface px-4 py-2 rounded-full">
        <PhoneIcon className="h-4 w-4 text-brand-blue" />
        Pilne pytania? Zadzwoń: <strong className="text-brand-ink">+48 511 487 431</strong>
      </div>
    </div>
  );
}
